import { useMutation, useQueryClient } from "@tanstack/react-query"
import { AlertCircle } from "lucide-react"
import { useId, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { CampoRif } from "./CampoRif"

/**
 * Alta y edición de un proveedor.
 *
 * ⚠ **El tipo sale de un catálogo, no de un campo de texto.** Es el cambio que trae
 * este módulo: en el 2.0 `proveedores.tipo` era texto libre y «Linea Naviera»,
 * «Línea naviera» y «naviera» eran tres tipos distintos, así que agrupar por
 * proveedor dejaba de funcionar sin que nadie lo notara. Cada tipo trae además su
 * **categoría**, que decide en qué selector aparece el proveedor: un
 * `ADMINISTRATIVO` no puede recibir un gasto del expediente —eso es margen de la
 * agencia y el importador no lo ve— y un `SERVICIO_PROPIO` no es un tercero.
 *
 * ⚠ **El RIF es opcional aquí y obligatorio en el cliente.** El 2.0 tiene servicios
 * básicos, condominios y personas naturales sin RIF registrado. Exigirlo obligaría a
 * inventárselo, que es peor que admitir que no se tiene.
 *
 * ⚠ **Un proveedor no puede ser su propio padre.** El servidor lo rechaza; el
 * selector además no se ofrece a sí mismo, que es lo que evita el intento.
 */

export type CategoriaProveedor = "OPERATIVO" | "ADMINISTRATIVO" | "SERVICIO_PROPIO"

export type TipoProveedor = {
  id: number
  nombre: string
  /** Una o varias: «Entes gubernamentales» es operativo y administrativo. */
  categorias: CategoriaProveedor[]
}

/** «OPERATIVO» → «operativo», para leerlo al lado del nombre del tipo. */
export function comoCategoria(c: CategoriaProveedor): string {
  return c.replace("_", " ").toLowerCase()
}

export type CondicionPago = "CONTADO" | "CREDITO"

export type Proveedor = {
  id: number
  codigo: string
  nombre: string
  rif: string | null
  tipo_proveedor_id: number | null
  /** Los otros tipos, cuando el proveedor es más de una cosa. */
  tipos_adicionales?: number[]
  proveedor_padre_id: number | null
  activo: boolean
  email: string | null
  telefono: string | null
  condicion_pago: CondicionPago
  dias_credito: number
  contribuyente_especial: boolean
  /** De fuera de Venezuela: no lleva RIF sino el identificador de su país. */
  extranjero?: boolean
  identificador_fiscal?: string | null
}

/** El listado más lo que sólo trae el detalle. Cómo se le paga va aparte: `datosPago.ts`. */
export type Detalle = Proveedor & {
  direccion: string | null
  alicuota_islr: string | null
}

type Props = {
  /** `null` para dar de alta. Al editar llega el DETALLE, con datos bancarios. */
  proveedor: Detalle | null
  tipos: TipoProveedor[]
  /** Para el selector de padre. El propio proveedor se excluye. */
  candidatosPadre: Proveedor[]
  onCerrar: () => void
  /**
   * Datos para PRELLENAR un alta —los que el lector de facturas leyó del papel—. Sólo
   * proponen: todo sigue editable, y el tipo lo elige una persona, porque es lo que
   * decide si el proveedor es administrativo.
   */
  sugerido?: {
    codigo?: string
    nombre?: string
    rif?: string
    extranjero?: boolean
    identificador_fiscal?: string
  }
  /** El proveedor recién guardado, para quien lo necesita en seguida (el lector). */
  onGuardado?: (proveedor: Proveedor) => void
}

export function FormularioProveedor({
  proveedor,
  tipos,
  candidatosPadre,
  onCerrar,
  sugerido,
  onGuardado,
}: Props) {
  const clienteQuery = useQueryClient()
  const idBase = useId()
  const editando = proveedor !== null

  const [codigo, setCodigo] = useState(proveedor?.codigo ?? sugerido?.codigo ?? "")
  const [nombre, setNombre] = useState(proveedor?.nombre ?? sugerido?.nombre ?? "")
  const [rif, setRif] = useState(proveedor?.rif ?? sugerido?.rif ?? "")
  const [extranjero, setExtranjero] = useState(
    proveedor?.extranjero ?? sugerido?.extranjero ?? false,
  )
  const [identificador, setIdentificador] = useState(
    proveedor?.identificador_fiscal ?? sugerido?.identificador_fiscal ?? "",
  )
  const [tipoId, setTipoId] = useState<number | null>(proveedor?.tipo_proveedor_id ?? null)
  const [adicionales, setAdicionales] = useState<number[]>(proveedor?.tipos_adicionales ?? [])
  const [padreId, setPadreId] = useState<number | null>(proveedor?.proveedor_padre_id ?? null)
  const [activo, setActivo] = useState(proveedor?.activo ?? true)
  const [email, setEmail] = useState(proveedor?.email ?? "")
  const [telefono, setTelefono] = useState(proveedor?.telefono ?? "")
  const [direccion, setDireccion] = useState(proveedor?.direccion ?? "")
  const [condicion, setCondicion] = useState<CondicionPago>(proveedor?.condicion_pago ?? "CONTADO")
  const [diasCredito, setDiasCredito] = useState(String(proveedor?.dias_credito ?? 0))
  const [especial, setEspecial] = useState(proveedor?.contribuyente_especial ?? false)

  const guardar = useMutation({
    mutationFn: () => {
      const comun = {
        nombre: nombre.trim(),
        // Un extranjero no lleva RIF: el servidor lo rechazaría (y lo borra al editar).
        rif: extranjero || rif.trim() === "" ? null : rif.trim(),
        extranjero,
        identificador_fiscal:
          extranjero && identificador.trim() !== "" ? identificador.trim() : null,
        tipo_proveedor_id: tipoId,
        // El principal nunca viaja también como adicional: el servidor lo quitaría,
        // y así la ficha manda exactamente lo que se ve marcado.
        tipos_adicionales: adicionales.filter((id) => id !== tipoId),
        proveedor_padre_id: padreId,
        email: email.trim() === "" ? null : email.trim(),
        telefono: telefono.trim() === "" ? null : telefono.trim(),
        direccion: direccion.trim() === "" ? null : direccion.trim(),
        condicion_pago: condicion,
        // Con CONTADO los días no significan nada: se guardan en cero en vez de
        // dejar un 30 viejo que nadie volverá a mirar.
        dias_credito: condicion === "CREDITO" ? Number(diasCredito || 0) : 0,
        contribuyente_especial: especial,
      }
      return editando
        ? api.patch(`/api/v1/proveedores/${proveedor.id}`, { ...comun, activo })
        : api.post<Proveedor>("/api/v1/proveedores", { ...comun, codigo: codigo.trim() })
    },
    onSuccess: (guardado) => {
      clienteQuery.invalidateQueries({ queryKey: ["proveedores"] })
      if (editando) clienteQuery.invalidateQueries({ queryKey: ["proveedor", proveedor.id] })
      if (guardado) onGuardado?.(guardado as Proveedor)
      onCerrar()
    },
  })

  const listo = nombre.trim().length >= 3 && (editando || codigo.trim().length >= 2)
  const borde = { borderColor: "var(--border)" }
  // Ofrecerse a sí mismo sería ofrecer un 409 con el motivo escrito.
  const padres = candidatosPadre.filter((p) => p.id !== proveedor?.id)

  return (
    <form
      aria-label={editando ? "Editar proveedor" : "Nuevo proveedor"}
      onSubmit={(e) => {
        e.preventDefault()
        if (listo) guardar.mutate()
      }}
      className="superficie p-4"
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block text-sm" htmlFor={`${idBase}-codigo`}>
          Código
          <input
            id={`${idBase}-codigo`}
            className="campo mt-1 disabled:opacity-60"
            value={codigo}
            disabled={editando}
            onChange={(e) => setCodigo(e.target.value)}
            placeholder="ALMCEN"
          />
        </label>

        <label className="block text-sm" htmlFor={`${idBase}-nombre`}>
          Nombre
          <input
            id={`${idBase}-nombre`}
            className="campo mt-1"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Almacenadora del Centro, C.A."
          />
        </label>

        {/* ⚠ **Un extranjero no tiene RIF, y no es un hueco.** Marcado, el RIF deja de
            pedirse —ni el solucionador lo cuenta— y se escribe el identificador de su
            país, como lo imprime: «EIN 12-3456789», «VAT GB123456789». */}
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input
            type="checkbox"
            checked={extranjero}
            onChange={(e) => setExtranjero(e.target.checked)}
          />
          Proveedor extranjero (fuera de Venezuela, sin RIF)
        </label>
        {extranjero ? (
          <label className="block text-sm" htmlFor={`${idBase}-identificador`}>
            Identificador fiscal de su país (opcional)
            <input
              id={`${idBase}-identificador`}
              className="campo mt-1"
              value={identificador}
              maxLength={60}
              onChange={(e) => setIdentificador(e.target.value)}
              placeholder="EIN 12-3456789 · VAT GB123456789"
            />
          </label>
        ) : (
          <CampoRif id={`${idBase}-rif`} etiqueta="RIF (opcional)" valor={rif} onCambiar={setRif} />
        )}

        <label className="block text-sm" htmlFor={`${idBase}-tipo`}>
          Tipo
          <SelectorBuscable
            id={`${idBase}-tipo`}
            opciones={tipos.map((t) => ({
              valor: t.id,
              etiqueta: `${t.nombre} · ${t.categorias.map(comoCategoria).join(" y ")}`,
            }))}
            valor={tipoId}
            onCambiar={setTipoId}
            vacio="Sin tipo"
          />
        </label>

        {/* ⚠ **Un proveedor puede ser más de una cosa**: un ente gubernamental al que
            además se le alquila un espacio, una naviera que también almacena. El
            principal es el que lo NOMBRA en los listados; éstos suman dónde se le
            puede usar. */}
        <fieldset className="block text-sm sm:col-span-2">
          <legend>También es</legend>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
            {tipos
              .filter((t) => t.id !== tipoId)
              .map((t) => (
                <label key={t.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={adicionales.includes(t.id)}
                    onChange={(e) =>
                      setAdicionales((actuales) =>
                        e.target.checked
                          ? [...actuales, t.id]
                          : actuales.filter((id) => id !== t.id),
                      )
                    }
                  />
                  {t.nombre}
                </label>
              ))}
          </div>
          <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
            Opcional. Suma dónde se le puede usar: el proveedor aparece en el selector de cualquiera
            de sus tipos.
          </p>
        </fieldset>

        <label className="block text-sm" htmlFor={`${idBase}-padre`}>
          Depende de
          <SelectorBuscable
            id={`${idBase}-padre`}
            opciones={padres.map((p) => ({
              valor: p.id,
              etiqueta: p.nombre,
              detalle: p.rif ?? undefined,
            }))}
            valor={padreId}
            onCambiar={setPadreId}
            vacio="Ninguno"
            placeholder="Nombre o RIF…"
          />
        </label>
        <p className="-mt-2 text-xs sm:col-start-2" style={{ color: "var(--fg-muted)" }}>
          Para agencias de una naviera o filiales de un grupo.
        </p>

        {editando && (
          <label className="flex items-center gap-2 self-end text-sm">
            <input type="checkbox" checked={activo} onChange={(e) => setActivo(e.target.checked)} />
            Activo
          </label>
        )}
      </div>

      <fieldset className="mt-4 rounded-[var(--radius-control)] border p-3" style={borde}>
        <legend className="px-1 text-sm font-medium">Contacto</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm" htmlFor={`${idBase}-email`}>
            Correo
            <input
              id={`${idBase}-email`}
              type="email"
              className="campo mt-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="cobranzas@proveedor.com"
            />
          </label>
          <label className="block text-sm" htmlFor={`${idBase}-telefono`}>
            Teléfono
            <input
              id={`${idBase}-telefono`}
              inputMode="tel"
              className="campo mt-1 tabular-nums"
              value={telefono}
              onChange={(e) => setTelefono(e.target.value)}
              placeholder="04141234567"
            />
          </label>
          <label className="block text-sm sm:col-span-2" htmlFor={`${idBase}-direccion`}>
            Dirección
            <input
              id={`${idBase}-direccion`}
              className="campo mt-1"
              value={direccion}
              onChange={(e) => setDireccion(e.target.value)}
            />
          </label>
        </div>
      </fieldset>

      <fieldset className="mt-4 rounded-[var(--radius-control)] border p-3" style={borde}>
        <legend className="px-1 text-sm font-medium">Condiciones y retenciones</legend>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block text-sm" htmlFor={`${idBase}-condicion`}>
            Condición de pago
            <select
              id={`${idBase}-condicion`}
              className="campo mt-1"
              value={condicion}
              onChange={(e) => setCondicion(e.target.value as CondicionPago)}
            >
              <option value="CONTADO">Contado</option>
              <option value="CREDITO">Crédito</option>
            </select>
          </label>
          {/* Sólo con crédito: preguntar los días de un pago de contado es pedir un
              dato que no existe, y alguien acaba escribiendo 0 o 30 al azar. */}
          {condicion === "CREDITO" && (
            <label className="block text-sm" htmlFor={`${idBase}-dias`}>
              Días de crédito
              <input
                id={`${idBase}-dias`}
                inputMode="numeric"
                className="campo mt-1 tabular-nums"
                value={diasCredito}
                onChange={(e) => setDiasCredito(e.target.value)}
              />
            </label>
          )}
          <label className="flex items-center gap-2 self-end text-sm">
            <input
              type="checkbox"
              checked={especial}
              onChange={(e) => setEspecial(e.target.checked)}
            />
            Contribuyente especial
          </label>
        </div>
        {/* La consecuencia, escrita: es lo que decide cuánto se le retiene, y se
            decide aquí y no en cada orden. */}
        <p className="mt-2 text-xs" style={{ color: "var(--fg-muted)" }}>
          Al contribuyente especial se le retiene el 100 % del IVA; al ordinario, el 75 %.
        </p>
      </fieldset>

      {/* ⚠ Los datos para pagarle ya no son dos casillas de este formulario (03/11):
          son varios —cuentas, pago móvil, Zelle, Binance— y viven en la ficha. */}
      <p className="mt-4 text-xs" style={{ color: "var(--fg-muted)" }}>
        Las cuentas, el pago móvil, Zelle o Binance para pagarle se cargan en su ficha, en «Datos de
        pago».
      </p>

      {guardar.isError && (
        <p
          role="alert"
          className="mt-3 flex items-center gap-2 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <AlertCircle size={16} aria-hidden={true} />
          {guardar.error instanceof ErrorApi
            ? guardar.error.message
            : "No se pudo guardar el proveedor."}
        </p>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCerrar}
          className="rounded-[var(--radius-control)] border px-3 py-1.5 text-sm"
          style={borde}
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={!listo || guardar.isPending}
          className="rounded-[var(--radius-control)] px-3 py-1.5 text-sm disabled:opacity-50"
          style={{ background: "var(--primary)", color: "var(--primary-fg)" }}
        >
          {guardar.isPending ? "Guardando…" : editando ? "Guardar cambios" : "Crear proveedor"}
        </button>
      </div>
    </form>
  )
}

/**
 * Los tipos del catálogo. Sin paginar: son una veintena y un selector no pagina.
 *
 * Se exporta la consulta y no un hook envolvente: `useQuery` se llama en la pantalla,
 * que es donde hace falta.
 */
export const CONSULTA_TIPOS = {
  queryKey: ["tipos-proveedor"],
  queryFn: () => api.get<TipoProveedor[]>("/api/v1/tipos-proveedor?activo=true"),
  staleTime: 5 * 60_000,
}
